import { c as _c } from "react/compiler-runtime";
import { useField } from 'formik';
import { useEffect, useState } from 'react';
import { useTheme } from 'styled-components';
import Button from '../../base/Button';
import Divider from '../../base/Divider';
import Text from '../../base/Text';
import EditorInput from '../../Form/Fields/EditorInput';
import TextAreaInput from '../../Form/Fields/TextAreaInput';
import { Div } from '../../helpers/StyledUtils';
const FocusableEditorInput: React.FC<{
  label: string;
  name: string;
  onSubmit: () => void;
  onReset: () => void;
  in_dropdown?: boolean;
  onBack?: () => void;
  validate?: (parsed: any) => string | undefined;
}> = ({
  label,
  name,
  in_dropdown,
  onBack,
  onSubmit,
  validate
}) => {
  const theme = useTheme();
  const [, {
    value,
    touched
  }, {
    setTouched,
    setValue
  }] = useField(name);
  const [error, setError] = useState<string | undefined>(undefined);
  useEffect(() => {
    try {
      const parsed = JSON.parse(value || '{}');
      setError(validate?.(parsed) ?? undefined);
    } catch (e) {
      setError('Invalid JSON');
    }
  }, [value, validate]);
  return <Div>
      {in_dropdown ? <Div flex={{
      align: 'center',
      gap: 3
    }} p={3}>
          <Button outline small icon="arrow_back" onClick={onBack} />
          <Text semi>{label}</Text>
        </Div> : <Div p={3}>{label && <Text semi>{label}</Text>}</Div>}
      <Divider />
      <Div style={{
      position: 'relative',
      backgroundColor: theme.colors.surface.base.variant_surface_3
    }}>
        <EditorInput onBlur={() => {
        setTouched(true);
        if (error) {
          setValue('{}');
        }
      }} display="minimal" height="232px" name={name} variant="dropdown" format_on_blur />
        {error && <Text p={{
        y: 2,
        x: 3,
        r: 20
      }} danger size="xs" style={{
        position: 'absolute',
        bottom: 0,
        left: 0
      }}>
            {error}
          </Text>}
      </Div>
    </Div>;
};
const validateHeaderValues = (parsed: any): string | undefined => {
  if (typeof parsed !== 'object' || Array.isArray(parsed)) {
    return 'Headers must be an object';
  }
  if (Object.values(parsed).some(value => typeof value !== 'string')) {
    return 'Header values must be strings';
  }
  return undefined;
};
export const JSONFilterHeaders: React.FC<{
  in_dropdown?: boolean;
  onBack?: () => void;
  onSubmit: () => void;
  onReset: () => void;
}> = t0 => {
  const $ = _c(5);
  const {
    onSubmit,
    onReset,
    in_dropdown,
    onBack
  } = t0;
  let t1;
  if ($[0] !== in_dropdown || $[1] !== onBack || $[2] !== onReset || $[3] !== onSubmit) {
    t1 = <FocusableEditorInput label="Filter by request header contents" name="request_headers" onSubmit={onSubmit} onReset={onReset} in_dropdown={in_dropdown} onBack={onBack} validate={validateHeaderValues} />;
    $[0] = in_dropdown;
    $[1] = onBack;
    $[2] = onReset;
    $[3] = onSubmit;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return t1;
};
export const JSONFilterBody: React.FC<{
  onSubmit: () => void;
  onReset: () => void;
}> = t0 => {
  const $ = _c(3);
  const {
    onSubmit,
    onReset
  } = t0;
  let t1;
  if ($[0] !== onReset || $[1] !== onSubmit) {
    t1 = <FocusableEditorInput label="Filter by request body contents" name="request_body" onSubmit={onSubmit} onReset={onReset} />;
    $[0] = onReset;
    $[1] = onSubmit;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
};
const validateQueryValues = (parsed: any): string | undefined => {
  if (typeof parsed !== 'object' || Array.isArray(parsed)) {
    return 'Query must be an object';
  }
  if (Object.values(parsed).some(value => typeof value !== 'string')) {
    return 'Query values must be strings';
  }
  return undefined;
};
export const JSONFilterQuery: React.FC<{
  in_dropdown?: boolean;
  onBack?: () => void;
  onSubmit: () => void;
  onReset: () => void;
}> = t0 => {
  const $ = _c(5);
  const {
    onSubmit,
    onReset,
    in_dropdown,
    onBack
  } = t0;
  let t1;
  if ($[0] !== in_dropdown || $[1] !== onBack || $[2] !== onReset || $[3] !== onSubmit) {
    t1 = <FocusableEditorInput label="Filter by request query contents" name="request_query" onSubmit={onSubmit} onReset={onReset} in_dropdown={in_dropdown} onBack={onBack} validate={validateQueryValues} />;
    $[0] = in_dropdown;
    $[1] = onBack;
    $[2] = onReset;
    $[3] = onSubmit;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return t1;
};
export const JSONFilterPath: React.FC<{
  in_dropdown?: boolean;
  onBack?: () => void;
  onSubmit: () => void;
  onReset: () => void;
}> = t0 => {
  const $ = _c(11);
  const {
    in_dropdown,
    onBack
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      direction: "column",
      gap: 3
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      align: "center",
      gap: 3
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== in_dropdown || $[3] !== onBack) {
    t3 = in_dropdown && <Button outline={true} small={true} icon="arrow_back" onClick={onBack} />;
    $[2] = in_dropdown;
    $[3] = onBack;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <Text semi={true}>Filter by request path contents</Text>;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t3) {
    t5 = <Div flex={t2}>{t3}{t4}</Div>;
    $[6] = t3;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <TextAreaInput m={0} name="request_path" placeholder="Path, e.g. /products" />;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== t5) {
    t7 = <Div><Div p={3} flex={t1}>{t5}{t6}</Div></Div>;
    $[9] = t5;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  return t7;
};